import {
    Box,
    Button,
    Card,
    CardBody,
    CardHeader,
    Center,
    Divider,
    Flex,
    FormControl,
    FormLabel,
    Heading,
    Icon,
    Select,
    SimpleGrid,
    Stack,
    Text,
    useToast
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import _find from "lodash/find";
import addressData from "../../data/address.json";
import DistictData from "../../data/District.json";
import { Roles } from "../../interfaes/user";
import { onGetUsers, getMyProfile } from "../../http/users";
import { DisabilityType } from "../../interfaes/student";
import { FiFilter } from "react-icons/fi";
import ModernLoader from "../../components/ModernLoader";
import SearchableSelect from "../../components/SearchableSelect";

export default function StudentProgressFilter() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const toast = useToast();

    // Filter states
    const [province, setProvince] = useState('');
    const [district, setDistrict] = useState('');
    const [districtId, setDistrictId] = useState(0);
    const [serviceArea, setServiceArea] = useState('');
    const [year, setYear] = useState('');
    const [teacherId, setTeacherId] = useState('');
    const [disabilityType, setDisabilityType] = useState('');
    const [disabilitySeverity, setDisabilitySeverity] = useState('');

    // Data states
    const [teachers, setTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);

    const address = addressData;
    const districts = DistictData;
    const disabilityTypeArray = Object.entries(DisabilityType);

    useEffect(() => {
        const fetchTeachers = async () => {
            try {
                const [usersResponse, profileResponse] = await Promise.all([
                    onGetUsers(),
                    getMyProfile()
                ]);

                const allUsers = usersResponse.data.userResponseList || [];
                const filteredTeachers = allUsers.filter((e) => e?.roles?.includes(Roles.TEACHER));
                setTeachers(filteredTeachers);

                const profile = profileResponse.data;
                setCurrentUser(profile);
                const isTeacher = profile.roles?.includes(Roles.TEACHER) && !profile.roles?.includes(Roles.ADMIN);

                if (isTeacher) {
                    setTeacherId(profile.id);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        fetchTeachers();
    }, []);

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (province) params.append('province', province);
        if (district) params.append('district', district);
        if (serviceArea) params.append('serviceArea', serviceArea);
        if (year) params.append('year', year);
        if (teacherId) params.append('teacherId', teacherId);
        if (disabilityType) params.append('disabilityType', disabilityType);
        if (disabilitySeverity) params.append('disabilitySeverity', disabilitySeverity);

        navigate(`/dashboard-student?${params.toString()}`);
    };

    const handleCancel = () => {
        setProvince('');
        setDistrict('');
        setDistrictId(0);
        setServiceArea('');
        setYear('');
        setTeacherId('');
        setDisabilityType('');
        setDisabilitySeverity('');
    };

    if (loading) {
        return <ModernLoader h="60vh" message="Loading filter options..." />;
    }

    return (
        <Box maxW="800px" mx="auto" py={8}>
            <Card variant="elevated" boxShadow="xl" borderRadius="20px" overflow="hidden">
                <CardHeader bg="blue.600" color="white" py={6}>
                    <Flex align="center">
                        <Icon as={FiFilter} mr={3} boxSize={6} />
                        <Heading size="lg">{t('common:filter') || 'Student Progress Filter'}</Heading>
                    </Flex>
                    <Text mt={2} opacity={0.9} fontSize="md">Select criteria to view student progress reports</Text>
                </CardHeader>
                <CardBody p={8}>
                    <Stack spacing={6}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                            <FormControl>
                                <FormLabel fontWeight="600">{t('studentLocal:manageStudents.province')}</FormLabel>
                                <SearchableSelect
                                    options={address.map(a => ({ label: a.name, value: a.name }))}
                                    value={province}
                                    onChange={(e) => {
                                        const found = _find(address, ['name', e.target.value]);
                                        setDistrictId(found ? found.id : 0);
                                        setProvince(e.target.value);
                                        setDistrict(''); // Reset district when province changes
                                    }}
                                    label={t('studentLocal:manageStudents.province')}
                                />
                            </FormControl>

                            <FormControl isDisabled={!province}>
                                <FormLabel fontWeight="600">{t('studentLocal:manageStudents.district')}</FormLabel>
                                <SearchableSelect
                                    options={districts.filter(o => o.province_id === districtId).map(a => ({ label: a.name, value: a.name }))}
                                    value={district}
                                    onChange={(e) => setDistrict(e.target.value)}
                                    isDisabled={!province}
                                    label={t('studentLocal:manageStudents.district')}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontWeight="600">{t('studentLocal:manageStudents.serviceArea')}</FormLabel>
                                <SearchableSelect
                                    options={['COMMUNITY', 'HOME', 'SCHOOL'].map(a => ({ label: a, value: a }))}
                                    value={serviceArea}
                                    onChange={(e) => setServiceArea(e.target.value)}
                                    label={t('studentLocal:manageStudents.serviceArea')}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontWeight="600">IEP Year</FormLabel>
                                <SearchableSelect
                                    options={['2080', '2081', '2082', '2083', '2084', '2085'].map(a => ({ label: a, value: a }))}
                                    value={year}
                                    onChange={(e) => setYear(e.target.value)}
                                    label="IEP Year"
                                />
                            </FormControl>

                            <FormControl isDisabled={currentUser?.roles?.includes(Roles.TEACHER) && !currentUser?.roles?.includes(Roles.ADMIN)}>
                                <FormLabel fontWeight="600">{t('users:TEACHER')}</FormLabel>
                                <SearchableSelect
                                    options={teachers.map(t => ({ label: t.displayName, value: t.id }))}
                                    value={teacherId}
                                    onChange={(e) => setTeacherId(e.target.value)}
                                    label={t('users:TEACHER')}
                                    isDisabled={currentUser?.roles?.includes(Roles.TEACHER) && !currentUser?.roles?.includes(Roles.ADMIN)}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontWeight="600">{t('studentLocal:manageStudents.disabilityType')}</FormLabel>
                                <SearchableSelect
                                    options={disabilityTypeArray.map(([key, value]) => ({ label: t(`studentLocal:manageStudents.${key}`), value: value }))}
                                    value={disabilityType}
                                    onChange={(e) => setDisabilityType(e.target.value)}
                                    label={t('studentLocal:manageStudents.disabilityType')}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontWeight="600">{t('studentLocal:manageStudents.disabilityId')}</FormLabel>
                                <SearchableSelect
                                    options={['Profound Disability (Red Color ID)', 'Severe Disability (Blue Color ID)', 'Moderate Disability (Yellow Color ID)', 'Mild Disability (White Color ID)', 'NO ID'].map(lang => ({ label: t(`studentLocal:manageStudents.${lang}`), value: lang }))}
                                    value={disabilitySeverity}
                                    onChange={(e) => setDisabilitySeverity(e.target.value)}
                                    label={t('studentLocal:manageStudents.disabilityId')}
                                />
                            </FormControl>
                        </SimpleGrid>

                        <Divider />

                        <Flex gap={4} justify="flex-end" pt={4}>
                            <Button
                                variant="ghost"
                                colorScheme="red"
                                size="lg"
                                px={10}
                                onClick={handleCancel}
                                borderRadius="12px"
                            >
                                {t('studentLocal:manageStudents.cancel') || 'Cancel'}
                            </Button>
                            <Button
                                colorScheme="blue"
                                size="lg"
                                px={12}
                                onClick={handleSearch}
                                borderRadius="12px"
                                boxShadow="md"
                                _hover={{ transform: 'translateY(-1px)', boxShadow: 'lg' }}
                            >
                                {t('studentLocal:search') || 'Search'}
                            </Button>
                        </Flex>
                    </Stack>
                </CardBody>
            </Card>
        </Box>
    );
}
